import { createReducer } from 'reduxsauce';
import { cloneDeep, filter, findIndex, isEqual } from 'lodash';
import moment from 'moment';

import { ShiftTypes } from '../actions/shift.actions';
import { ShiftStatus } from '@rapid-recovery-agency-inc/insightt-shared';

const initialState = {
  status: [],
  workHours: null,
  isOnShift: false,
  shiftCheckListStatus: null,
  shiftId: null,
  shiftTimeId: null,
  shiftTimeStart: null,
  shiftTimeType: null,
  isOnPause: false,
  pauseTimeId: null,
  pauseTimeStart: null,
  totalPauseTimes: 0,
  breaks: [],
  isOnBreak: false,
  breakTimeId: null,
  startedBreak: null,
  breakTimeEnd: null,
  motionTracker: null,
  successStartShift: null,
  beingIdle: false,
  coords: null,
  durations: null,
  repossessionsOverview: null,
  repossessionsGoals: null,
  repossessionsHitList: [],
  spottedVehicle: null,
  commissionRequests: [],
  shiftInfractions: [],
  shiftPendingInfractions: [],
  successSubmitInactivityAnswer: false,
  successUpdateInactivityAnswer: false,
};

const getShiftStatusRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-get-shift-status');

  return { ...state, status };
};
const getShiftStatusSuccess = (state, action) => {
  const {
    shiftStatus,
    shiftWorkHours,
    shiftBreaks,
    shiftMotionTracker,
    shiftPendingInfractions,
    checkListStatus,
  } = action.response;
  let workHours = null;
  if (shiftWorkHours) {
    const { startTime, endTime } = shiftWorkHours;
    workHours = {
      startTime: moment(startTime).valueOf(),
      endTime: moment(endTime).valueOf(),
    };
  }

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-status', a));

  return {
    ...state,
    isOnShift: shiftStatus.isOnShift,
    shiftTimeId: shiftStatus.shiftTimeId,
    shiftId: shiftStatus.shiftId,
    shiftTimeStart: shiftStatus.shiftTimeStart,
    shiftTimeType: shiftStatus.shiftTimeType,
    isOnPause: shiftStatus?.status === ShiftStatus.PAUSED,
    pauseTimeId: shiftStatus.pause && shiftStatus.pause.pauseTimeId,
    pauseTimeStart: shiftStatus.pause && shiftStatus.pause.pauseTimeStart,
    totalPauseTimes: shiftStatus.totalPauseTimes && shiftStatus.totalPauseTimes,
    isOnBreak: shiftStatus?.status === ShiftStatus.BREAKING,
    breakTimeId: shiftStatus.break && shiftStatus.break.breakTimeId,
    startedBreak: shiftStatus.break && shiftStatus.break.selectedBreak,
    breakTimeEnd: shiftStatus.break && shiftStatus.break.breakTimeEnd,
    workHours,
    breaks: shiftBreaks,
    motionTracker: shiftMotionTracker,
    shiftPendingInfractions,
    status,
    shiftCheckListStatus: checkListStatus,
  };
};
const changeShiftCheckListStatus = (state, action) => {
  const { status } = action;
  return {
    ...state,
    shiftCheckListStatus: status,
  };
};
const getShiftStatusFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-status', a));

  return { ...state, status };
};

const startShiftRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-start-shift');

  return { ...state, status };
};
const startShiftSuccess = (state, action) => {
  const { shiftTime, shiftWorkHours, shiftBreaks, shiftMotionTracker } = action.response;

  let workHours = null;
  if (shiftWorkHours) {
    const { startTime, endTime } = shiftWorkHours;
    workHours = {
      startTime: moment(startTime).valueOf(),
      endTime: moment(endTime).valueOf(),
    };
  }

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-start-shift', a));

  return {
    ...state,
    successStartShift: true,
    isOnShift: true,
    shiftTimeId: shiftTime.id,
    shiftId: shiftTime.shiftId,
    shiftTimeStart: shiftTime.startTime,
    shiftTimeType: shiftTime.shiftType,
    workHours,
    breaks: shiftBreaks,
    motionTracker: shiftMotionTracker,
    status,
  };
};

const startShiftFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-start-shift', a));

  return { ...state, status };
};

const pauseShiftRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-pause-shift');

  return { ...state, status };
};
const pauseShiftSuccess = (state, action) => {
  const { pauseTimeId, pauseTimeStart } = action;

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-pause-shift', a));

  return {
    ...state,
    isOnPause: true,
    pauseTimeId,
    pauseTimeStart,
    status,
  };
};
const pauseShiftFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-pause-shift', a));

  return { ...state, status };
};

const resumeShiftRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-resume-shift');

  return { ...state, status };
};
const resumeShiftSuccess = (state, action) => {
  const { totalPauseTimes } = action;

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-resume-shift', a));

  return {
    ...state,
    isOnPause: false,
    pauseTimeId: null,
    pauseTimeStart: null,
    totalPauseTimes,
    status,
  };
};
const resumeShiftFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-resume-shift', a));

  return { ...state, status };
};

const takeBreakRequest = (state, action) => {
  const { startedBreak } = action;
  const status = cloneDeep(state.status);
  status.push(`pending-take-break-${startedBreak.id}`);

  return { ...state, status };
};
const takeBreakSuccess = (state, action) => {
  const { breakTimeId, startedBreak } = action;

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual(`pending-take-break-${startedBreak.id}`, a));

  return {
    ...state,
    isOnBreak: true,
    breakTimeId,
    startedBreak,
    breakTimeEnd: Date.now() + startedBreak.allowedTime * 60 * 1000,
    status,
  };
};
const takeBreakFailure = (state, action) => {
  const { startedBreak } = action;
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual(`pending-take-break-${startedBreak.id}`, a));

  return { ...state, status };
};

const endBreakRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-end-break');

  return { ...state, status };
};
const endBreakSuccess = (state, action) => {
  let breaks = cloneDeep(state.breaks);
  breaks = filter(breaks, (e) => !isEqual(e.id, action.breakId));

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-end-break', a));

  return {
    ...state,
    breaks,
    isOnBreak: false,
    breakTimeId: null,
    startedBreak: {},
    breakTimeEnd: null,
    status,
  };
};
const endBreakFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-end-break', a));

  return { ...state, status };
};

const trackLocationRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-track-location');

  return { ...state, status };
};
const trackLocationSuccess = (state, action) => {
  const { beingIdle, coords, durations } = action.response;

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-track-location', a));

  return {
    ...state,
    beingIdle,
    coords,
    durations,
    status,
  };
};
const trackLocationFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-track-location', a));

  return { ...state, status };
};

const endShiftRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-end-shift');

  return { ...state, status };
};
const endShiftSuccess = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-end-shift', a));
  return {
    ...state,
    ...initialState,
    status,
  };
};
const endShiftFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-end-shift', a));

  return { ...state, status };
};

const getShiftRepossessionsOverviewRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-get-shift-repossessions-overview');

  return { ...state, status };
};
const getShiftRepossessionsOverviewSuccess = (state, action) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-repossessions-overview', a));

  return { ...state, repossessionsOverview: action.response, status };
};
const getShiftRepossessionsOverviewFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-repossessions-overview', a));

  return { ...state, status };
};

const getShiftRepossessionsGoalsRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-get-shift-repossessions-goals');

  return { ...state, status };
};
const getShiftRepossessionsGoalsSuccess = (state, action) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-repossessions-goals', a));

  return { ...state, repossessionsGoals: action.response, status };
};
const getShiftRepossessionsGoalsFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-repossessions-goals', a));

  return { ...state, status };
};

const getShiftRepossessionsHitListRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-get-shift-repossessions-hitlist');

  return { ...state, status };
};
const getShiftRepossessionsHitListSuccess = (state, action) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-repossessions-hitlist', a));

  return { ...state, repossessionsHitList: action.response, status };
};
const getShiftRepossessionsHitListFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-repossessions-hitlist', a));

  return { ...state, status };
};

const getSpottedVehicleByVinRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-get-spotted-vehicle');

  return { ...state, status };
};
const getSpottedVehicleByVinSuccess = (state, action) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-spotted-vehicle', a));

  return { ...state, spottedVehicle: action.response, status };
};
const getSpottedVehicleByVinFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-spotted-vehicle', a));

  return { ...state, status };
};

const submitSpottedVehicleRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-submit-spotted-vehicle');

  return { ...state, status };
};
const submitSpottedVehicleSuccess = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-submit-spotted-vehicle', a));

  return { ...state, spottedVehicle: null, status };
};
const submitSpottedVehicleFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-submit-spotted-vehicle', a));

  return { ...state, status };
};

const submitCustomCommissionRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-submit-custom-commission');

  return { ...state, status };
};
const submitCustomCommissionSuccess = (state, action) => {
  const commissionRequests = cloneDeep(state.commissionRequests);
  if (action.response) commissionRequests.push(action.response);

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-submit-custom-commission', a));

  return { ...state, commissionRequests, status };
};
const submitCustomCommissionFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-submit-custom-commission', a));

  return { ...state, status };
};

const getCustomCommissionsRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-get-commission-requests');

  return { ...state, status };
};
const getCustomCommissionsSuccess = (state, action) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-commission-requests', a));

  return {
    ...state,
    commissionRequests: action.response,
    status,
  };
};
const getCustomCommissionsFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-commission-requests', a));

  return { ...state, status };
};

const updateCustomCommissionRequest = (state, action) => {
  const { commissionId } = action;

  const status = cloneDeep(state.status);
  status.push(`pending-update-custom-commission-${commissionId}`);

  return { ...state, status };
};
const updateCustomCommissionSuccess = (state, action) => {
  const commissionRequests = cloneDeep(state.commissionRequests);
  const idx = findIndex(commissionRequests, (c) => isEqual(c.id, action.response.id));
  if (idx > -1) {
    commissionRequests[idx] = action.response;
  }

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual(`pending-update-custom-commission-${action.response.id}`, a));

  return { ...state, commissionRequests, status };
};
const updateCustomCommissionFailure = (state, action) => {
  const { commissionId } = action;

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual(`pending-update-custom-commission-${commissionId}`, a));

  return { ...state, status };
};

const cancelCustomCommissionRequest = (state, action) => {
  const { commissionId } = action;

  const status = cloneDeep(state.status);
  status.push(`pending-cancel-custom-commission-${commissionId}`);

  return { ...state, status };
};
const cancelCustomCommissionSuccess = (state, action) => {
  const { commissionId } = action;

  let commissionRequests = cloneDeep(state.commissionRequests);
  commissionRequests = filter(commissionRequests, (c) => !isEqual(c.id, commissionId));

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual(`pending-cancel-custom-commission-${commissionId}`, a));

  return { ...state, commissionRequests, status };
};
const cancelCustomCommissionFailure = (state, action) => {
  const { commissionId } = action;

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual(`pending-cancel-custom-commission-${commissionId}`, a));

  return { ...state, status };
};

const getShiftInfractionsRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-get-shift-infractions');

  return { ...state, status };
};
const getShiftInfractionsSuccess = (state, action) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-infractions', a));

  return {
    ...state,
    shiftInfractions: action.response,
    status,
  };
};
const getShiftInfractionsFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-infractions', a));

  return { ...state, status };
};

const getShiftPendingInfractionsRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-get-shift-pending-infractions');

  return { ...state, status };
};
const getShiftPendingInfractionsSuccess = (state, action) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-pending-infractions', a));

  return {
    ...state,
    shiftPendingInfractions: action.response,
    status,
  };
};
const getShiftPendingInfractionsFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-get-shift-pending-infractions', a));

  return { ...state, status };
};

const submitInfractionAnswerRequest = (state) => {
  const status = cloneDeep(state.status);
  status.push('pending-submit-infraction-answer');

  return { ...state, status };
};
const submitInfractionAnswerSuccess = (state, action) => {
  let shiftPendingInfractions = cloneDeep(state.shiftPendingInfractions);
  shiftPendingInfractions = filter(shiftPendingInfractions, (e) => !isEqual(e.id, action.response.id));

  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-submit-infraction-answer', a));

  return {
    ...state,
    shiftPendingInfractions,
    successSubmitInfractionAnswer: true,
    status,
  };
};
const submitInfractionAnswerFailure = (state) => {
  let status = cloneDeep(state.status);
  status = filter(status, (a) => !isEqual('pending-submit-infraction-answer', a));

  return { ...state, status };
};

const clearSuccessStartShiftRequest = (state) => ({
  ...state,
  successStartShift: null,
});

const clearBeingIdleStatusRequest = (state) => ({ ...state, beingIdle: false });

const clearSpottedVehicleRequest = (state) => ({
  ...state,
  spottedVehicle: null,
});

const clearSubmitInfractionAnswerRequest = (state) => ({
  ...state,
  successSubmitInfractionAnswer: false,
});

const clearShiftRequest = (state) => ({ ...state, ...initialState });

export const reducer = createReducer(initialState, {
  [ShiftTypes.GET_SHIFT_STATUS_REQUEST]: getShiftStatusRequest,
  [ShiftTypes.GET_SHIFT_STATUS_SUCCESS]: getShiftStatusSuccess,
  [ShiftTypes.GET_SHIFT_STATUS_FAILURE]: getShiftStatusFailure,

  [ShiftTypes.CHANGE_SHIFT_CHECK_LIST_STATUS]: changeShiftCheckListStatus,

  [ShiftTypes.START_SHIFT_REQUEST]: startShiftRequest,
  [ShiftTypes.START_SHIFT_SUCCESS]: startShiftSuccess,
  [ShiftTypes.START_SHIFT_FAILURE]: startShiftFailure,

  [ShiftTypes.PAUSE_SHIFT_REQUEST]: pauseShiftRequest,
  [ShiftTypes.PAUSE_SHIFT_SUCCESS]: pauseShiftSuccess,
  [ShiftTypes.PAUSE_SHIFT_FAILURE]: pauseShiftFailure,

  [ShiftTypes.RESUME_SHIFT_REQUEST]: resumeShiftRequest,
  [ShiftTypes.RESUME_SHIFT_SUCCESS]: resumeShiftSuccess,
  [ShiftTypes.RESUME_SHIFT_FAILURE]: resumeShiftFailure,

  [ShiftTypes.TAKE_BREAK_REQUEST]: takeBreakRequest,
  [ShiftTypes.TAKE_BREAK_SUCCESS]: takeBreakSuccess,
  [ShiftTypes.TAKE_BREAK_FAILURE]: takeBreakFailure,

  [ShiftTypes.END_BREAK_REQUEST]: endBreakRequest,
  [ShiftTypes.END_BREAK_SUCCESS]: endBreakSuccess,
  [ShiftTypes.END_BREAK_FAILURE]: endBreakFailure,

  [ShiftTypes.END_SHIFT_REQUEST]: endShiftRequest,
  [ShiftTypes.END_SHIFT_SUCCESS]: endShiftSuccess,
  [ShiftTypes.END_SHIFT_FAILURE]: endShiftFailure,

  [ShiftTypes.TRACK_LOCATION_REQUEST]: trackLocationRequest,
  [ShiftTypes.TRACK_LOCATION_SUCCESS]: trackLocationSuccess,
  [ShiftTypes.TRACK_LOCATION_FAILURE]: trackLocationFailure,

  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_OVERVIEW_REQUEST]: getShiftRepossessionsOverviewRequest,
  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_OVERVIEW_SUCCESS]: getShiftRepossessionsOverviewSuccess,
  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_OVERVIEW_FAILURE]: getShiftRepossessionsOverviewFailure,

  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_GOALS_REQUEST]: getShiftRepossessionsGoalsRequest,
  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_GOALS_SUCCESS]: getShiftRepossessionsGoalsSuccess,
  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_GOALS_FAILURE]: getShiftRepossessionsGoalsFailure,

  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_HIT_LIST_REQUEST]: getShiftRepossessionsHitListRequest,
  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_HIT_LIST_SUCCESS]: getShiftRepossessionsHitListSuccess,
  [ShiftTypes.GET_SHIFT_REPOSSESSIONS_HIT_LIST_FAILURE]: getShiftRepossessionsHitListFailure,

  [ShiftTypes.GET_SPOTTED_VEHICLE_BY_VIN_REQUEST]: getSpottedVehicleByVinRequest,
  [ShiftTypes.GET_SPOTTED_VEHICLE_BY_VIN_SUCCESS]: getSpottedVehicleByVinSuccess,
  [ShiftTypes.GET_SPOTTED_VEHICLE_BY_VIN_FAILURE]: getSpottedVehicleByVinFailure,

  [ShiftTypes.SUBMIT_SPOTTED_VEHICLE_REQUEST]: submitSpottedVehicleRequest,
  [ShiftTypes.SUBMIT_SPOTTED_VEHICLE_SUCCESS]: submitSpottedVehicleSuccess,
  [ShiftTypes.SUBMIT_SPOTTED_VEHICLE_FAILURE]: submitSpottedVehicleFailure,

  [ShiftTypes.SUBMIT_CUSTOM_COMMISSION_REQUEST]: submitCustomCommissionRequest,
  [ShiftTypes.SUBMIT_CUSTOM_COMMISSION_SUCCESS]: submitCustomCommissionSuccess,
  [ShiftTypes.SUBMIT_CUSTOM_COMMISSION_FAILURE]: submitCustomCommissionFailure,

  [ShiftTypes.GET_CUSTOM_COMMISSIONS_REQUEST]: getCustomCommissionsRequest,
  [ShiftTypes.GET_CUSTOM_COMMISSIONS_SUCCESS]: getCustomCommissionsSuccess,
  [ShiftTypes.GET_CUSTOM_COMMISSIONS_FAILURE]: getCustomCommissionsFailure,

  [ShiftTypes.UPDATE_CUSTOM_COMMISSION_REQUEST]: updateCustomCommissionRequest,
  [ShiftTypes.UPDATE_CUSTOM_COMMISSION_SUCCESS]: updateCustomCommissionSuccess,
  [ShiftTypes.UPDATE_CUSTOM_COMMISSION_FAILURE]: updateCustomCommissionFailure,

  [ShiftTypes.CANCEL_CUSTOM_COMMISSION_REQUEST]: cancelCustomCommissionRequest,
  [ShiftTypes.CANCEL_CUSTOM_COMMISSION_SUCCESS]: cancelCustomCommissionSuccess,
  [ShiftTypes.CANCEL_CUSTOM_COMMISSION_FAILURE]: cancelCustomCommissionFailure,

  [ShiftTypes.GET_SHIFT_INFRACTIONS_REQUEST]: getShiftInfractionsRequest,
  [ShiftTypes.GET_SHIFT_INFRACTIONS_SUCCESS]: getShiftInfractionsSuccess,
  [ShiftTypes.GET_SHIFT_INFRACTIONS_FAILURE]: getShiftInfractionsFailure,

  [ShiftTypes.GET_SHIFT_PENDING_INFRACTIONS_REQUEST]: getShiftPendingInfractionsRequest,
  [ShiftTypes.GET_SHIFT_PENDING_INFRACTIONS_SUCCESS]: getShiftPendingInfractionsSuccess,
  [ShiftTypes.GET_SHIFT_PENDING_INFRACTIONS_FAILURE]: getShiftPendingInfractionsFailure,

  [ShiftTypes.SUBMIT_INFRACTION_ANSWER_REQUEST]: submitInfractionAnswerRequest,
  [ShiftTypes.SUBMIT_INFRACTION_ANSWER_SUCCESS]: submitInfractionAnswerSuccess,
  [ShiftTypes.SUBMIT_INFRACTION_ANSWER_FAILURE]: submitInfractionAnswerFailure,

  [ShiftTypes.CLEAR_SUCCESS_START_SHIFT_REQUEST]: clearSuccessStartShiftRequest,
  [ShiftTypes.CLEAR_BEING_IDLE_STATUS_REQUEST]: clearBeingIdleStatusRequest,
  [ShiftTypes.CLEAR_SPOTTED_VEHICLE_REQUEST]: clearSpottedVehicleRequest,
  [ShiftTypes.CLEAR_SUBMIT_INFRACTION_ANSWER_REQUEST]: clearSubmitInfractionAnswerRequest,

  [ShiftTypes.CLEAR_SHIFT_REQUEST]: clearShiftRequest,
});
